import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { queryWishlistMaterials } from '../../../../api/wishlist/query-wishlist-materials';
import { DEFAULT_SORTING_KEYS } from '../../../../constants/search-sorting';
import session from '../../../../utils/session';

const PAGE_SIZE = 10;
const SORTING = DEFAULT_SORTING_KEYS.CREATED_AT_DESC;

const fetchPage = (p: number, limit = PAGE_SIZE) =>
    queryWishlistMaterials({ limit, p, s: SORTING } as unknown as SearchState, {
        filter: '{ isFree: false }'
    });

// The wishlist summary mirrors only { id } into Redux; a toggle made in this tab puts the whole material first.
const isWholeMaterial = (item?: Material): item is Material => Boolean(item?.title);

type UseFavoritesReturn = {
    materials: Material[];
    hasMoreToFetch: boolean;
    onFetchNextPage: VoidFunction;
    onRemove: (material: Material) => void;
};

const useFavorites = (): UseFavoritesReturn => {
    const [materials, setMaterials] = useState<Material[]>([]);
    const [total, setTotal] = useState(0);

    const hasFetched = useRef<boolean>(false);
    const prevFavoriteTotalRef = useRef<number>(0);

    const favoriteList = useSelector((state: RootState) => state.main.favorite.list);
    // The total grows only when a material is added; a refetch of the loaded page can grow the list without one.
    const favoriteTotal = useSelector((state: RootState) => state.main.favorite.total);
    const favoriteLoaded = useSelector((state: RootState) => state.main.favorite.loaded);

    const handleFetchFirstPage = (): void => {
        hasFetched.current = true;
        prevFavoriteTotalRef.current = favoriteTotal;

        fetchPage(1).then(({ total: serverTotal, wishlist }) => {
            setTotal(serverTotal);
            setMaterials(wishlist);
        });
    };

    const handleAddMaterial = (): void => {
        const addedMaterial = favoriteList[0];
        const isPaidAndNotAcquired = isWholeMaterial(addedMaterial) && !addedMaterial.isFree && !addedMaterial.aclId;

        prevFavoriteTotalRef.current = favoriteTotal;

        if (!isPaidAndNotAcquired) {
            return;
        }

        setTotal((prevTotal) => prevTotal + 1);
        setMaterials((prev) => [addedMaterial, ...prev]);
    };

    useEffect(() => {
        if (!hasFetched.current) {
            if (!favoriteLoaded || !session.isLoggedIn()) {
                return;
            }

            if (favoriteTotal > 0) {
                handleFetchFirstPage();

                return;
            }

            hasFetched.current = true;
            prevFavoriteTotalRef.current = 0;

            return;
        }

        if (favoriteTotal > prevFavoriteTotalRef.current) {
            handleAddMaterial();

            return;
        }

        prevFavoriteTotalRef.current = favoriteTotal;
    }, [favoriteLoaded, favoriteTotal]);

    const onFetchNextPage = (): void => {
        const nextPage = Math.floor(materials.length / PAGE_SIZE) + 1;
        const offset = materials.length % PAGE_SIZE;

        fetchPage(nextPage).then(({ wishlist }) => setMaterials((prev) => [...prev, ...wishlist.slice(offset)]));
    };

    const onRemove = (material: Material): void => {
        const newMaterials = materials.filter((m) => m.id !== material.id);
        const newTotal = total - 1;

        setTotal(newTotal);
        setMaterials(newMaterials);

        const shouldFetchNextMaterial = newMaterials.length < newTotal;

        if (!shouldFetchNextMaterial) {
            return;
        }

        fetchPage(newMaterials.length + 1, 1).then(({ wishlist }) => setMaterials([...newMaterials, wishlist[0]]));
    };

    const hasMoreToFetch = materials.length < total;

    return { materials, hasMoreToFetch, onFetchNextPage, onRemove };
};

export default useFavorites;
